/**
 * The end-user directory, as decisions — AUTH-09.
 *
 * The page renders these; the tests hold them. What is here is everything
 * that is a judgement rather than a layout: which of several empty lists a
 * customer is looking at, what signing one person out actually costs, and
 * how to describe somebody who has never signed in.
 *
 * THE THREE EMPTY LISTS
 *
 * They look identical and mean entirely different things, and a page that
 * renders one message for all of them sends somebody looking for a fault
 * that is not there:
 *
 *   auth has never been turned on   - there is no directory yet
 *   nobody has signed up            - the directory is working and empty
 *   the search matched nobody       - there are users, just not that one
 */

import type { EndUser } from './api'

export type DirectoryState =
  | { kind: 'not_enabled' }
  | { kind: 'no_users' }
  | { kind: 'no_matches'; search: string }
  | { kind: 'users'; users: EndUser[] }

export function directoryState(input: {
  enabled: boolean
  users: EndUser[]
  search: string
}): DirectoryState {
  if (!input.enabled) return { kind: 'not_enabled' }
  if (input.users.length > 0) return { kind: 'users', users: input.users }
  if (input.search.trim() !== '') return { kind: 'no_matches', search: input.search.trim() }
  return { kind: 'no_users' }
}

export function emptyMessage(state: DirectoryState): string {
  switch (state.kind) {
    case 'not_enabled':
      return 'Auth is not turned on for this project yet, so nobody can sign up. '
        + 'Turn it on above and this becomes the list of people who have.'
    case 'no_users':
      return 'Nobody has signed up yet. When somebody does, they appear here — '
        + 'these are your users, in your database, not accounts with us.'
    case 'no_matches':
      return `No address matches “${state.search}”. This project does have users; `
        + 'clear the search to see them.'
    case 'users':
      return ''
  }
}

/**
 * How somebody gets in.
 *
 * 'credential' is Better Auth's word and it means a password. Showing it
 * raw would be the one term on the page a customer has to look up.
 */
export function signInMethod(providers: string[]): string {
  if (providers.length === 0) return 'No sign-in method'
  return providers
    .map((p) => (p === 'credential' ? 'Password' : p.charAt(0).toUpperCase() + p.slice(1)))
    .join(', ')
}

/** "Signed in right now", said in a way that does not overstate what we know. */
export function sessionLabel(live: number): string {
  if (live === 0) return 'Not signed in'
  return live === 1 ? '1 device' : `${live} devices`
}

/**
 * What signing one person out does, and the bit that surprises people.
 *
 * The third line is the honest one. The project-wide revoke also deletes
 * the signing keys, which is what stops tokens already issued; doing that
 * for one person would sign out everybody else's tokens too, so this one
 * cannot. Somebody acting on a compromised account needs to know the gap
 * exists before they close the tab.
 */
export const REVOKE_USER_CONSEQUENCES = [
  'They are signed out on every device, immediately.',
  'Their account, password and data are untouched — they can sign back in.',
  'A data token they already hold keeps working for up to 15 more minutes. '
    + 'Ending every session in the project is what stops those sooner.',
]

/** Nobody else is affected, which is the whole point of the narrow verb. */
export const REVOKE_USER_SAFE = 'No other user is signed out.'

export function revokeUserPrompt(user: Pick<EndUser, 'email'>): string {
  return `Sign ${user.email} out of every device?`
}

/**
 * The stronger verb, and why it is a separate one — AUTH-09.
 *
 * Signing somebody out and switching their account off sit next to each
 * other in the same list and read almost the same, so the consequence that
 * distinguishes them - can they get back in by themselves - leads.
 */
export const DISABLE_USER_CONSEQUENCES = [
  'They cannot sign in again until you turn the account back on.',
  'They are signed out on every device now, as well.',
  'Their account, password and data are untouched, and nothing is deleted.',
  'A data token they already hold keeps working for up to 5 more minutes.',
]

export const ENABLE_USER_CONSEQUENCES = [
  'They can sign in again.',
  'The sessions they had before are not restored - they sign in fresh.',
]

export const DISABLE_USER_SAFE = 'No other user is affected, and this can be undone.'

export function disablePrompt(user: Pick<EndUser, 'email'>, disabled: boolean): string {
  return disabled
    ? `Switch ${user.email}'s account back on?`
    : `Stop ${user.email} signing in?`
}

/** What the row says about an account that is off. */
export const DISABLED_BADGE = 'Disabled'

/** Verified, said plainly, with the caveat this project actually has. */
export function verificationLabel(user: Pick<EndUser, 'emailVerified'>): string {
  return user.emailVerified ? 'Address confirmed' : 'Address not confirmed'
}

/**
 * Deliberately not a warning.
 *
 * requireEmailVerification is off, so an unconfirmed address does not block
 * anything. Styling it as a problem would invite somebody to go looking for
 * a setting that would lock out every account predating verification.
 */
export const UNVERIFIED_NOTE = 'Unconfirmed addresses can still sign in — '
  + 'confirmation is recorded, not enforced.'
